import {
  IContractAgreementResponse,
  IContractCustomSectionResponse,
  IContractDistributionResponse,
} from 'services/contract/types';
import { AGREEMENT_TYPE } from 'services/templates/agreement-template/types';
import {
  IContractTemplateTreeItemForm,
  IDistributionType,
  TEMPLATE_TYPE,
} from 'services/templates/types';

export const distributionSectionQueryToForm = (
  distributionSection: IContractDistributionResponse,
  templateType: IDistributionType,
  extremeId?: number
): IContractTemplateTreeItemForm => {
  const distributionChildren: IContractTemplateTreeItemForm[] =
    distributionSection.shares.map(
      ({ salesChannelId, percent, salesChannelName }, key) => ({
        id: `share-${TEMPLATE_TYPE[templateType]}-${
          extremeId ? extremeId + key + 1 : key + 2
        }`,
        type: TEMPLATE_TYPE.agreement,
        content: {
          type: AGREEMENT_TYPE.shares,
          distributionTemplateShares: {
            percent: percent,
            id: salesChannelId,
            name: salesChannelName ?? '',
          },
        },
        children: [],
      })
    );

  distributionChildren.unshift({
    id: `default-share-${TEMPLATE_TYPE[templateType]}`,
    type: TEMPLATE_TYPE.agreement,
    content: {
      type: AGREEMENT_TYPE.defaultShare,
      distributionTemplateShares: {
        percent: distributionSection.defaultSharePercentage,
        id: `default-share-agreement-${TEMPLATE_TYPE[templateType]}`,
        name: '',
      },
    },
    children: [],
    disabledSelection: true,
  });

  const distribution: IContractTemplateTreeItemForm = {
    id: `distribution-${TEMPLATE_TYPE[templateType]}`,
    type: templateType,
    content: distributionSection.title,
    children: distributionChildren,
  };

  return distribution;
};

interface IConnectContractTreeItems {
  customSections?: IContractCustomSectionResponse[];
  digitalDistribution?: IContractDistributionResponse;
  physicalDistribution?: IContractDistributionResponse;
  merchDistribution?: IContractDistributionResponse;
  channelDistribution?: IContractDistributionResponse;
  agreements?: IContractAgreementResponse[];
}

export const connectContractTreeItems = (
  contractItems: IConnectContractTreeItems,
  lowestAvailableId?: string
): IContractTemplateTreeItemForm[] => {
  const {
    agreements,
    customSections,
    digitalDistribution,
    physicalDistribution,
    channelDistribution,
    merchDistribution,
  } = contractItems;

  const treeItems: IContractTemplateTreeItemForm[] = [];

  let lowestId = lowestAvailableId ? parseInt(lowestAvailableId) + 1 : 0;

  if (agreements?.length) {
    for (const agreement of agreements) {
      const { position, value, title } = agreement;
      treeItems[position] = {
        id: `${lowestId}`,
        type: TEMPLATE_TYPE.agreement,
        children: [],
        content: {
          type: AGREEMENT_TYPE.text,
          selectAgreementTemplate: null,
          textAgreementTemplate: { defaultValue: value, title },
        },
      };

      lowestId += 1;
    }
  }

  if (digitalDistribution && !!digitalDistribution.title.length) {
    const digitalDistributionItem = distributionSectionQueryToForm(
      digitalDistribution,
      TEMPLATE_TYPE.digitalDistribution
    );
    treeItems[digitalDistribution.position] = digitalDistributionItem;
    lowestId += digitalDistributionItem.children.length + 1;
  }

  if (physicalDistribution && !!physicalDistribution.title.length) {
    const physicalDistributionItem = distributionSectionQueryToForm(
      physicalDistribution,
      TEMPLATE_TYPE.physicalDistribution
    );
    treeItems[physicalDistribution.position] = physicalDistributionItem;
    lowestId += physicalDistributionItem.children.length + 1;
  }
  if (merchDistribution && !!merchDistribution.title.length) {
    const merchDistributionItem = distributionSectionQueryToForm(
      merchDistribution,
      TEMPLATE_TYPE.merchDistribution
    );
    treeItems[merchDistribution.position] = merchDistributionItem;
    lowestId += merchDistributionItem.children.length + 1;
  }
  if (channelDistribution && !!channelDistribution.title.length) {
    const channelDistributionItem = distributionSectionQueryToForm(
      channelDistribution,
      TEMPLATE_TYPE.channelDistribution
    );
    treeItems[channelDistribution.position] = channelDistributionItem;
    lowestId += channelDistributionItem.children.length + 1;
  }

  if (customSections?.length) {
    for (const section of customSections) {
      const children = connectContractTreeItems(
        {
          agreements: section.agreement,
          customSections: section.customSections,
        },
        `${lowestId + 1}`
      );

      treeItems[section.position] = {
        id: `${lowestId}`,
        type: TEMPLATE_TYPE.section,
        content: section.title,
        children: children,
      };

      lowestId += children.length + 2;
    }
  }

  return treeItems;
};
